import React, { useState } from 'react';
import Select from 'react-select';
import { postGeneralInquiry } from '../../api';
import countryOptions from '../../assets/countries.json';
import './Contact.css';

// grecaptcha is loaded globally via the script tag in public/index.html
declare const grecaptcha: {
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  inquiry: string;
  country: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  inquiry?: string;
  country?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-().]{7,20}$/;

const EMPTY_FORM: FormData = {
  name: '', email: '', phone: '', company: '', inquiry: '', country: '',
};

const validate = (data: FormData): FormErrors => {
  const errors: FormErrors = {};
  if (!data.name.trim()) errors.name = 'Name is required.';
  if (!data.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_REGEX.test(data.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!data.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!PHONE_REGEX.test(data.phone)) {
    errors.phone = 'Enter a valid phone number (7–20 digits, may include +, spaces, dashes).';
  }
  if (!data.inquiry.trim()) errors.inquiry = 'Please describe your inquiry.';
  if (!data.country) errors.country = 'Please select your country.';
  return errors;
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCountryChange = (selectedOption: { value: string; label: string } | null) => {
    setFormData((prev) => ({ ...prev, country: selectedOption ? selectedOption.label : '' }));
    if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const recaptchaToken = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit_general_inquiry' });
      await postGeneralInquiry({ ...formData, recaptchaToken });
      setFormData(EMPTY_FORM);
      setErrors({});
      setIsSuccess(true);
    } catch {
      setSubmitError(
        'Failed to submit your inquiry. Please try again or reach us directly at info@ravitaj.com.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSuccess(false);
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
  };

  return (
    <section className="contact" id="contact">
      <div className="contact-container">
        <h2>Contact Us</h2>
        <div className="contact-content">
          {/* ── Left info panel ── */}
          <div className="contact-info">
            <h3>Get in Touch</h3>
            <p>Interested in our products? We'd love to hear from you.</p>
            <div className="contact-details">
              <p><strong>Email:</strong> info@ravitaj.com</p>
              <p><strong>Phone / WhatsApp:</strong> +91 9624797077</p>
              <p><strong>Location:</strong> Ahmedabad, Gujarat, India - 380060</p>
            </div>
            <a
              href={`https://wa.me/919624797077?text=${encodeURIComponent(
                'Hello, I am interested in your Ayurvedic herbal products. Could you please share more details?'
              )}`}
              className="contact-whatsapp-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
              </svg>
              Chat on WhatsApp
            </a>
          </div>

          {/* ── Right form / success ── */}
          {isSuccess ? (
            <div className="contact-success">
              <div className="contact-success-icon">✓</div>
              <h3>Message Received!</h3>
              <p>
                Thank you for reaching out. We will review your inquiry and get back to you within
                1–2 business days.
              </p>
              <button className="contact-submit-button" onClick={handleReset}>
                Send Another Inquiry
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone / WhatsApp * <small>(e.g. +1 555 000 0000)</small></label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="company">Company (Optional)</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <Select
                  inputId="country"
                  options={countryOptions}
                  onChange={handleCountryChange}
                  value={countryOptions.find((o) => o.label === formData.country) ?? null}
                  placeholder="Select your country"
                  isSearchable
                />
                {errors.country && <span className="field-error">{errors.country}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="inquiry">Inquiry *</label>
                <textarea
                  id="inquiry"
                  name="inquiry"
                  value={formData.inquiry}
                  onChange={handleChange}
                  rows={5}
                />
                {errors.inquiry && <span className="field-error">{errors.inquiry}</span>}
              </div>

              <p className="recaptcha-notice">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                apply.
              </p>

              {submitError && <p className="submit-error">{submitError}</p>}

              <button type="submit" className="contact-submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending…' : 'Submit Inquiry'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
